import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowSquareOut,
  CheckCircle,
  Globe,
  Info,
  UploadSimple,
  WarningCircle,
} from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui-kit/button';
import { Card, CardContent } from '@/components/ui-kit/card';
import { Checkbox } from '@/components/ui-kit/checkbox';
import { Input } from '@/components/ui-kit/input';
import { Label } from '@/components/ui-kit/label';
import { Switch } from '@/components/ui-kit/switch';
import { useToast } from '@/hooks/use-toast';
import { HttpError } from '@/lib/https';
import { cn } from '@/lib/utils';
import {
  useGetBrandingSettings,
  useGetLegalFooterSettings,
  useUpdateBrandingSettings,
  useUploadBrandingLogo,
} from '../hooks/use-compliance-settings';
import { useGetOptInSettings } from '../hooks/use-opt-in-settings';
import { getFiles } from '@/lib/api/services/storage.service';
import { getCampaignApiUrl } from '@/lib/api/campaign-api-client';
import type { BrandingLocale as Locale } from '../types/compliance-settings.types';

const PLATFORM_ACCENT = '#0066B2';
const LOCALES = [
  { value: 'de', label: 'Deutsch' },
  { value: 'fr', label: 'Français' },
  { value: 'it', label: 'Italiano' },
  { value: 'en', label: 'English' },
] as const;

const LOCALE_LABELS: Record<string, string> = {
  de: 'Deutsch',
  fr: 'Français',
  it: 'Italiano',
  en: 'English',
};

const PREVIEW_STRINGS: Record<string, Record<string, string>> = {
  en: {
    language: 'Language',
    poweredBy: 'Powered by SELISE Campaign',
    unsubDoneTitle: 'You’ve been unsubscribed',
    unsubDoneBody:
      'You won’t receive any more marketing emails from {name}. It can take a few minutes to apply everywhere.',
    continueTo: 'Continue to {name}',
    confirmedTitle: 'You’re subscribed',
    confirmedBody: 'Thanks for confirming. You’ll now receive emails from {name}.',
  },
  de: {
    language: 'Sprache',
    poweredBy: 'Bereitgestellt von SELISE Campaign',
    unsubDoneTitle: 'Sie wurden abgemeldet',
    unsubDoneBody:
      'Sie erhalten keine Marketing-E-Mails mehr von {name}. Es kann einige Minuten dauern, bis dies überall gilt.',
    continueTo: 'Weiter zu {name}',
    confirmedTitle: 'Ihre Anmeldung ist bestätigt',
    confirmedBody: 'Danke für Ihre Bestätigung. Sie erhalten ab jetzt E-Mails von {name}.',
  },
  fr: {
    language: 'Langue',
    poweredBy: 'Propulsé par SELISE Campaign',
    unsubDoneTitle: 'Vous êtes désinscrit·e',
    unsubDoneBody:
      'Vous ne recevrez plus d’e-mails marketing de {name}. L’application partout peut prendre quelques minutes.',
    continueTo: 'Continuer vers {name}',
    confirmedTitle: 'Votre inscription est confirmée',
    confirmedBody: 'Merci de votre confirmation. Vous recevrez désormais les e-mails de {name}.',
  },
  it: {
    language: 'Lingua',
    poweredBy: 'Offerto da SELISE Campaign',
    unsubDoneTitle: 'Iscrizione annullata',
    unsubDoneBody:
      'Non riceverai più e-mail di marketing da {name}. Potrebbero volerci alcuni minuti perché valga ovunque.',
    continueTo: 'Continua su {name}',
    confirmedTitle: 'Iscrizione confermata',
    confirmedBody: 'Grazie per la conferma. D’ora in poi riceverai le e-mail di {name}.',
  },
};

const getPreviewTranslation = (
  locale: string,
  key: string,
  vars?: Record<string, string>
) => {
  const dict = PREVIEW_STRINGS[locale] || PREVIEW_STRINGS.en;
  let text = dict[key] ?? PREVIEW_STRINGS.en[key] ?? '';
  if (vars) {
    for (const [k, v] of Object.entries(vars)) {
      text = text.replace(new RegExp(`\\{${k}\\}`, 'g'), v);
    }
  }
  return text;
};

type BrandingState = {
  displayName: string;
  logoMediaId: string;
  logoUrl: string;
  accentColor: string;
  enabledLocales: Locale[];
  defaultLocale: Locale;
  unsubscribedRedirectUrl: string;
  showPoweredBy: boolean;
};

type BrandingErrors = Partial<
  Record<
    'accentColor' | 'enabledLocales' | 'defaultLocale' | 'unsubscribedRedirectUrl' | 'logo',
    string
  >
>;

const INITIAL_BRANDING: BrandingState = {
  displayName: '',
  logoMediaId: '',
  logoUrl: '',
  accentColor: '',
  enabledLocales: ['de', 'fr', 'it', 'en'],
  defaultLocale: 'en',
  unsubscribedRedirectUrl: '',
  showPoweredBy: true,
};

export const contrastOnWhite = (hex: string) => {
  const match = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!match) return null;
  const value = Number.parseInt(match[1], 16);
  const channels = [(value >> 16) & 255, (value >> 8) & 255, value & 255].map((channel) => {
    const normalized = channel / 255;
    return normalized <= 0.03928 ? normalized / 12.92 : ((normalized + 0.055) / 1.055) ** 2.4;
  });
  const luminance = 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2];
  return Math.round((1.05 / (luminance + 0.05)) * 100) / 100;
};

export const validateBranding = (branding: BrandingState): BrandingErrors => {
  const errors: BrandingErrors = {};
  if (branding.accentColor) {
    const contrast = contrastOnWhite(branding.accentColor);
    if (contrast === null) errors.accentColor = 'Use a 6-digit hex colour, e.g. #B4234E.';
    else if (contrast < 4.5)
      errors.accentColor = `Too light for white button text (${contrast}:1). It needs at least 4.5:1.`;
  }
  if (
    branding.unsubscribedRedirectUrl &&
    !/^https:\/\/[^\s]+\.[^\s]+/i.test(branding.unsubscribedRedirectUrl)
  ) {
    errors.unsubscribedRedirectUrl = 'Enter a full https:// address, or leave it empty.';
  }
  if (!branding.enabledLocales.length) errors.enabledLocales = 'Enable at least one language.';
  else if (!branding.enabledLocales.includes(branding.defaultLocale)) {
    errors.defaultLocale = 'The default language must be one of the enabled languages.';
  }
  return errors;
};

const FieldError = ({ children }: { children?: string }) =>
  children ? (
    <p className="mt-1 flex items-center gap-1 text-xs font-medium text-destructive" role="alert">
      <WarningCircle size={13} className="shrink-0" />
      <span>{children}</span>
    </p>
  ) : null;

const brandingErrorsFrom = (error: unknown): BrandingErrors => {
  if (!(error instanceof HttpError) || error.status !== 400) return {};
  const raw = Array.isArray(error.error.errors) ? error.error.errors : [];
  const mapped: BrandingErrors = {};
  for (const entry of raw) {
    if (typeof entry !== 'string') continue;
    const [prefix, ...message] = entry.split(':');
    const value = message.join(':').trim() || entry;
    switch (prefix.trim().toLowerCase()) {
      case 'accentcolor':
        mapped.accentColor = value;
        break;
      case 'enabledlocales':
        mapped.enabledLocales = value;
        break;
      case 'defaultlocale':
        mapped.defaultLocale = value;
        break;
      case 'unsubscribedredirecturl':
        mapped.unsubscribedRedirectUrl = value;
        break;
      case 'logomediaid':
        mapped.logo = value;
        break;
    }
  }
  return mapped;
};

export const BrandingSettingsPage = () => {
  const { toast } = useToast();
  const fileRef = useRef<HTMLInputElement>(null);
  const { data } = useGetBrandingSettings();
  const { data: footerData } = useGetLegalFooterSettings();
  const { data: optInSettings } = useGetOptInSettings();
  const updateBranding = useUpdateBrandingSettings();
  const uploadLogoMutation = useUploadBrandingLogo();
  const [branding, setBranding] = useState(INITIAL_BRANDING);
  const [errors, setErrors] = useState<BrandingErrors>({});
  const [previewTab, setPreviewTab] = useState<'unsubscribe' | 'confirmation'>('unsubscribe');
  const [previewLocale, setPreviewLocale] = useState<Locale>('en');
  const [displayedLogoUrl, setDisplayedLogoUrl] = useState('');
  const [savedNote, setSavedNote] = useState('');
  const [saveServerError, setSaveServerError] = useState(false);

  useEffect(() => {
    if (!data) return;
    setBranding({
      displayName: data.displayName,
      logoMediaId: data.logoMediaId,
      logoUrl: data.logoUrl,
      accentColor: data.accentColor,
      enabledLocales: data.enabledLocales,
      defaultLocale: data.defaultLocale,
      unsubscribedRedirectUrl: data.unsubscribedRedirectUrl,
      showPoweredBy: data.showPoweredBy,
    });
    setPreviewLocale(data.defaultLocale);
  }, [data]);

  useEffect(() => {
    const logoUrl = branding.logoUrl;
    const logoMediaId = branding.logoMediaId;

    if (logoUrl) {
      const fullUrl = /^(?:https?:|blob:|data:)/i.test(logoUrl)
        ? logoUrl
        : getCampaignApiUrl(logoUrl);
      setDisplayedLogoUrl(fullUrl);
      setErrors((current) => ({ ...current, logo: undefined }));
      return;
    }

    if (!logoMediaId) {
      setDisplayedLogoUrl('');
      return;
    }

    let active = true;
    const rawId = logoMediaId.trim();
    const cleanId = rawId.includes('/') ? rawId.split('/').pop() || rawId : rawId;

    void getFiles([cleanId])
      .then((res) => {
        if (!active) return;
        const matched = res.data?.find(
          (item) => (item.itemId || '').trim() === cleanId || (item.url || '').trim()
        );
        const resolvedUrl = matched?.url || res.data?.[0]?.url;
        if (resolvedUrl) {
          setDisplayedLogoUrl(resolvedUrl);
          setErrors((current) => ({ ...current, logo: undefined }));
        } else {
          setDisplayedLogoUrl('');
          setErrors((current) => ({ ...current, logo: 'Could not load the saved logo.' }));
        }
      })
      .catch(() => {
        if (active) {
          setDisplayedLogoUrl('');
          setErrors((current) => ({ ...current, logo: 'Could not load the saved logo.' }));
        }
      });

    return () => {
      active = false;
    };
  }, [branding.logoUrl, branding.logoMediaId]);

  const accent = branding.accentColor || PLATFORM_ACCENT;
  const contrast = useMemo(() => contrastOnWhite(accent), [accent]);
  const displayNamePlaceholder = footerData?.companyName || 'SELISE Digital Platforms AG';
  const resolvedName =
    branding.displayName.trim() || data?.resolvedName || footerData?.companyName || 'Our newsletter';

  const activePreviewLocale = useMemo(() => {
    if (previewLocale && branding.enabledLocales.includes(previewLocale)) {
      return previewLocale;
    }
    if (branding.defaultLocale && branding.enabledLocales.includes(branding.defaultLocale)) {
      return branding.defaultLocale;
    }
    return branding.enabledLocales[0] || 'en';
  }, [previewLocale, branding.enabledLocales, branding.defaultLocale]);

  const update = <K extends keyof BrandingState>(key: K, value: BrandingState[K]) => {
    setSavedNote('');
    setSaveServerError(false);
    setBranding((current) => {
      const next = { ...current, [key]: value };
      setErrors(validateBranding(next));
      return next;
    });
  };

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    const nextErrors = validateBranding(branding);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      toast({
        variant: 'destructive',
        description: 'Fix the highlighted fields before saving.',
      });
      return;
    }
    try {
      await updateBranding.mutateAsync({
        displayName: branding.displayName.trim(),
        logoMediaId: branding.logoMediaId,
        accentColor: branding.accentColor.trim(),
        enabledLocales: branding.enabledLocales,
        defaultLocale: branding.defaultLocale,
        unsubscribedRedirectUrl: branding.unsubscribedRedirectUrl.trim(),
        showPoweredBy: branding.showPoweredBy,
      });
      setSavedNote('Saved just now.');
      setSaveServerError(false);
      toast({
        variant: 'success',
        description: 'Branding saved. The public pages use it from now on.',
      });
    } catch (error) {
      const apiErrors = brandingErrorsFrom(error);
      if (Object.keys(apiErrors).length) {
        setErrors(apiErrors);
        toast({
          variant: 'destructive',
          description: 'Fix the highlighted fields before saving.',
        });
      } else {
        setSaveServerError(true);
        toast({
          variant: 'destructive',
          description:
            error instanceof Error
              ? error.message
              : 'Something went wrong. Your changes have not been lost — try Save again.',
        });
      }
    }
  };

  const toggleLocale = (locale: Locale, checked: boolean) => {
    const enabledLocales = checked
      ? [...branding.enabledLocales, locale]
      : branding.enabledLocales.filter((item) => item !== locale);
    update('enabledLocales', enabledLocales);
    if (!enabledLocales.includes(previewLocale) && enabledLocales.length)
      setPreviewLocale(enabledLocales[0]);
  };

  const uploadLogo = async (file?: File) => {
    if (!file) return;
    if (!['image/png', 'image/jpeg'].includes(file.type)) {
      setErrors((current) => ({ ...current, logo: 'Use a PNG or JPG file.' }));
      return;
    }
    if (file.size > 1024 * 1024) {
      setErrors((current) => ({ ...current, logo: 'Logo files must be 1 MB or smaller.' }));
      return;
    }
    try {
      const uploaded = await uploadLogoMutation.mutateAsync(file);
      setBranding((current) => ({
        ...current,
        logoMediaId: uploaded.itemId,
        logoUrl: uploaded.url,
      }));
      setDisplayedLogoUrl(uploaded.url);
      setErrors((current) => ({ ...current, logo: undefined }));
    } catch (error) {
      setErrors((current) => ({
        ...current,
        logo: error instanceof Error ? error.message : 'Could not upload logo.',
      }));
    }
  };

  const openLiveHref =
    previewTab === 'unsubscribe' ? '/unsubscribe?state=success' : '/subscription-confirm';

  return (
    <div className="mx-auto flex w-full max-w-[1120px] flex-col gap-5 pb-8">
      <div>
        <div className="mb-2 flex items-center gap-1.5 text-sm text-muted-foreground">
          <Link to="/settings" className="hover:text-foreground">
            Settings
          </Link>
          <span>/</span>
          <span className="font-medium text-foreground">Branding</span>
        </div>
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">Branding</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          How the pages look when someone unsubscribes or confirms their subscription. Your
          customers see your brand here, not ours.
        </p>
      </div>

      <div
        className="flex items-start gap-2.5 rounded-lg border border-[#CCE1F0] px-3.5 py-3"
        style={{ background: '#E6F0F8' }}
      >
        <Info size={16} className="mt-0.5 shrink-0" style={{ color: '#0066B2' }} />
        <div className="text-[13px] leading-[18px]" style={{ color: '#003F6F' }}>
          Branding changes how these pages <strong>look</strong> and where people go{' '}
          <strong>afterwards</strong>. It never changes what they do: every unsubscribe and
          confirmation is recorded by us, before any redirect.
          <span className="block mt-1">
            Coming later (MVP-2): serving these pages from your own domain (e.g.{' '}
            <span className="font-mono">email.yourbrand.com</span>), and hosting them inside your
            own app through the API.
          </span>
        </div>
      </div>

      <form
        onSubmit={save}
        noValidate
        className="grid items-start gap-5 lg:grid-cols-[minmax(0,1fr)_420px]"
      >
        <div className="flex min-w-0 flex-col gap-5">
          <Card className="rounded-lg border-neutral-200 bg-white shadow-sm">
            <CardContent className="flex flex-col gap-4 p-5 sm:p-6">
              <h2 className="text-lg font-semibold text-foreground">Brand</h2>
              <div>
                <Label htmlFor="branding-name">Display name</Label>
                <Input
                  id="branding-name"
                  maxLength={80}
                  value={branding.displayName}
                  placeholder={displayNamePlaceholder}
                  onChange={(e) => update('displayName', e.target.value)}
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  Shown on the pages and in &ldquo;Continue to &hellip;&rdquo;. Leave blank to use your legal name from
                  the{' '}
                  <Link
                    to="/settings/legal-footer"
                    className="font-medium text-primary hover:underline"
                  >
                    Legal footer
                  </Link>
                  .
                </p>
              </div>
              <div>
                <Label>Logo</Label>
                <div className="flex flex-wrap items-center gap-3">
                  <div className="grid h-14 w-40 place-items-center overflow-hidden rounded-md border border-dashed border-neutral-300 bg-neutral-50 text-xs text-neutral-500">
                    {displayedLogoUrl ? (
                      <img
                        src={displayedLogoUrl}
                        alt="Current logo"
                        className="max-h-12 max-w-[150px] object-contain"
                      />
                    ) : (
                      'No logo — the name is shown'
                    )}
                  </div>
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/png,image/jpeg"
                    className="sr-only"
                    aria-label="Upload logo"
                    onChange={(e) => void uploadLogo(e.target.files?.[0])}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    loading={uploadLogoMutation.isPending}
                    disabled={uploadLogoMutation.isPending}
                    onClick={() => fileRef.current?.click()}
                  >
                    <UploadSimple size={16} />
                    Upload
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    disabled={!branding.logoMediaId && !branding.logoUrl}
                    onClick={() => {
                      update('logoMediaId', '');
                      update('logoUrl', '');
                      setDisplayedLogoUrl('');
                      setErrors((current) => ({ ...current, logo: undefined }));
                    }}
                  >
                    Remove
                  </Button>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  PNG or JPG. In the product this comes from the media library; the prototype keeps
                  it in this browser (max 200 KB).
                </p>
                <FieldError>{errors.logo}</FieldError>
              </div>
              <div>
                <Label htmlFor="branding-accent">Accent colour</Label>
                <div className="flex flex-wrap items-center gap-2.5">
                  <input
                    type="color"
                    value={accent}
                    aria-label="Pick accent colour"
                    className="h-10 w-11 cursor-pointer rounded-md border border-input bg-white p-1"
                    onChange={(e) => update('accentColor', e.target.value.toUpperCase())}
                  />
                  <Input
                    id="branding-accent"
                    aria-label="Accent colour"
                    className="w-36 font-mono"
                    maxLength={7}
                    value={branding.accentColor}
                    placeholder={PLATFORM_ACCENT}
                    onChange={(e) => update('accentColor', e.target.value)}
                    aria-invalid={!!errors.accentColor}
                  />
                  {contrast !== null && (
                    <span
                      className={
                        contrast >= 4.5
                          ? 'inline-flex items-center gap-1 text-xs font-medium text-green-700'
                          : 'text-xs font-medium text-destructive'
                      }
                    >
                      {contrast >= 4.5 && <CheckCircle size={14} />} {contrast}:1 ·{' '}
                      {contrast >= 4.5 ? 'passes' : 'too light'}
                    </span>
                  )}
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => update('accentColor', '')}
                  >
                    Use platform default
                  </Button>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  Colours the main button, whose text is white — so it needs at least 4.5:1
                  contrast.
                </p>
                <FieldError>{errors.accentColor}</FieldError>
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-lg border-neutral-200 bg-white shadow-sm">
            <CardContent className="flex flex-col gap-4 p-5 sm:p-6">
              <h2 className="text-lg font-semibold text-foreground">Languages</h2>
              <fieldset>
                <legend className="mb-2 text-sm font-medium">
                  Languages the pages are offered in
                </legend>
                <div className="flex flex-wrap gap-4">
                  {LOCALES.map((locale) => (
                    <label
                      key={locale.value}
                      className="flex cursor-pointer items-center gap-2 text-sm"
                    >
                      <Checkbox
                        checked={branding.enabledLocales.includes(locale.value)}
                        onCheckedChange={(checked) => toggleLocale(locale.value, checked === true)}
                      />
                      {locale.label}
                    </label>
                  ))}
                </div>
                <FieldError>{errors.enabledLocales}</FieldError>
              </fieldset>
              <div>
                <Label htmlFor="branding-default-locale">Default language</Label>
                <select
                  id="branding-default-locale"
                  value={branding.defaultLocale}
                  onChange={(e) => update('defaultLocale', e.target.value as Locale)}
                  className="h-10 min-w-52 rounded-md border border-input bg-white px-3 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
                >
                  {LOCALES.map((locale) => (
                    <option
                      key={locale.value}
                      value={locale.value}
                      disabled={!branding.enabledLocales.includes(locale.value)}
                    >
                      {locale.label}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-muted-foreground">
                  Used when we don&apos;t know the recipient&apos;s language. Otherwise the page opens in
                  their language, with a switcher.
                </p>
                <FieldError>{errors.defaultLocale}</FieldError>
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-lg border-neutral-200 bg-white shadow-sm">
            <CardContent className="flex flex-col gap-4 p-5 sm:p-6">
              <h2 className="text-lg font-semibold text-foreground">After the action</h2>
              <div>
                <Label htmlFor="branding-redirect">
                  After unsubscribing, send people to{' '}
                  <span className="font-normal text-muted-foreground">(optional)</span>
                </Label>
                <Input
                  id="branding-redirect"
                  value={branding.unsubscribedRedirectUrl}
                  placeholder="https://www.example.com"
                  onChange={(e) => update('unsubscribedRedirectUrl', e.target.value.trim())}
                  aria-invalid={!!errors.unsubscribedRedirectUrl}
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  Adds a &ldquo;Continue to {resolvedName}&rdquo; button once the unsubscribe is recorded.
                  Where people go after <em>confirming</em> is set in{' '}
                  <Link
                    to="/settings/optin"
                    className="font-medium text-primary hover:underline"
                  >
                    Double opt-in
                  </Link>
                  .
                </p>
                <FieldError>{errors.unsubscribedRedirectUrl}</FieldError>
              </div>
              <div className="flex items-center justify-between gap-3 border-t border-neutral-100 pt-4">
                <div>
                  <p className="text-sm font-medium">Show &ldquo;Powered by SELISE Campaign&rdquo;</p>
                  <p className="text-xs text-muted-foreground">
                    A small line under the page. It is the only place our name appears.
                  </p>
                </div>
                <Switch
                  checked={branding.showPoweredBy}
                  onCheckedChange={(checked) => update('showPoweredBy', checked)}
                  aria-label='Show "Powered by SELISE Campaign"'
                />
              </div>
            </CardContent>
          </Card>

          {saveServerError ? (
            <div
              id="saveServerError"
              className="flex items-start gap-2.5 rounded-lg px-3.5 py-3 text-[13px] leading-[18px]"
              style={{ background: '#FCE4EA', color: '#8F0022' }}
            >
              <WarningCircle size={16} className="mt-0.5 shrink-0" style={{ color: '#D80032' }} />
              <div>Something went wrong. Your changes have not been lost — try Save again.</div>
            </div>
          ) : null}

          <div className="sticky bottom-0 flex items-center justify-between gap-3 flex-wrap bg-[#FAFAFA] pt-3 pb-1">
            <div className="text-xs text-neutral-500">
              {savedNote ? <span>{savedNote}</span> : null}
            </div>
            <Button
              id="btnSave"
              type="submit"
              loading={updateBranding.isPending}
              disabled={updateBranding.isPending || uploadLogoMutation.isPending}
            >
              Save changes
            </Button>
          </div>
        </div>

        {/* Live preview matching prototype */}
        <aside className="flex flex-col gap-3 lg:sticky lg:top-0" aria-label="Preview">
          <div className="flex flex-wrap items-center gap-2">
            <div
              className="inline-flex rounded-md border border-neutral-200 bg-white p-0.5"
              role="tablist"
              aria-label="Preview page"
            >
              <button
                type="button"
                role="tab"
                aria-selected={previewTab === 'unsubscribe'}
                onClick={() => setPreviewTab('unsubscribe')}
                className={cn(
                  'rounded px-3 py-1.5 text-xs font-medium transition-colors',
                  previewTab === 'unsubscribe'
                    ? 'bg-[#E6F0F8] text-[#005494]'
                    : 'text-neutral-600 hover:text-neutral-900'
                )}
              >
                Unsubscribe
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={previewTab === 'confirmation'}
                onClick={() => setPreviewTab('confirmation')}
                className={cn(
                  'rounded px-3 py-1.5 text-xs font-medium transition-colors',
                  previewTab === 'confirmation'
                    ? 'bg-[#E6F0F8] text-[#005494]'
                    : 'text-neutral-600 hover:text-neutral-900'
                )}
              >
                Confirmation
              </button>
            </div>
            <select
              id="previewLocale"
              aria-label="Preview language"
              value={activePreviewLocale}
              onChange={(e) => setPreviewLocale(e.target.value as Locale)}
              className="h-8 w-auto rounded-md border border-neutral-200 bg-white px-2.5 py-0 text-xs text-neutral-900 focus:outline-none focus:border-primary-600"
            >
              {branding.enabledLocales.map((loc) => (
                <option key={loc} value={loc}>
                  {LOCALE_LABELS[loc] || loc}
                </option>
              ))}
            </select>
            <div className="flex-1" />
            <a
              id="openLive"
              href={openLiveHref}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
            >
              Open page
              <ArrowSquareOut size={13} className="shrink-0" />
            </a>
          </div>

          <div className="flex flex-col gap-3 rounded-lg border border-neutral-200 bg-neutral-100 p-4">
            <div
              id="previewCard"
              className="rounded-lg border border-neutral-200 bg-white p-6 text-center shadow-[0_2px_4px_rgba(16,24,40,0.08)]"
            >
              {displayedLogoUrl ? (
                <div className="mb-5 flex items-center justify-center" data-brand-header>
                  <img
                    src={displayedLogoUrl}
                    alt="Brand logo preview"
                    className="max-h-12 max-w-[200px] object-contain"
                  />
                </div>
              ) : (
                <div className="mb-5 flex items-center justify-center gap-2" data-brand-header>
                  <span
                    className="grid size-9 shrink-0 place-items-center rounded-md text-base font-semibold text-white"
                    style={{ background: accent }}
                  >
                    {resolvedName.trim().charAt(0).toUpperCase() || '?'}
                  </span>
                  <span className="truncate text-base font-semibold text-neutral-900">
                    {resolvedName}
                  </span>
                </div>
              )}

              <span
                className="mx-auto mb-4 grid size-12 place-items-center rounded-full"
                style={{ background: '#E7F4EC' }}
              >
                <CheckCircle size={22} weight="regular" style={{ color: '#1F8A46' }} />
              </span>

              <h3 className="text-xl font-semibold leading-7 text-neutral-900">
                {previewTab === 'unsubscribe'
                  ? getPreviewTranslation(activePreviewLocale, 'unsubDoneTitle', {
                      name: resolvedName,
                    })
                  : getPreviewTranslation(activePreviewLocale, 'confirmedTitle', {
                      name: resolvedName,
                    })}
              </h3>

              <p className="mt-1.5 text-sm leading-5 text-neutral-600">
                {previewTab === 'unsubscribe'
                  ? getPreviewTranslation(activePreviewLocale, 'unsubDoneBody', {
                      name: resolvedName,
                    })
                  : getPreviewTranslation(activePreviewLocale, 'confirmedBody', {
                      name: resolvedName,
                    })}
              </p>

              {previewTab === 'unsubscribe' && branding.unsubscribedRedirectUrl ? (
                <div className="mt-5">
                  <button
                    type="button"
                    tabIndex={-1}
                    aria-hidden="true"
                    className="h-10 w-full rounded-md px-4 text-sm font-medium text-white transition-opacity hover:opacity-95"
                    style={{ background: accent, borderColor: accent }}
                  >
                    {getPreviewTranslation(activePreviewLocale, 'continueTo', {
                      name: resolvedName,
                    })}
                  </button>
                </div>
              ) : null}

              {previewTab === 'confirmation' && optInSettings?.confirmedRedirectUrl ? (
                <div className="mt-5">
                  <button
                    type="button"
                    tabIndex={-1}
                    aria-hidden="true"
                    className="h-10 w-full rounded-md px-4 text-sm font-medium text-white transition-opacity hover:opacity-95"
                    style={{ background: accent, borderColor: accent }}
                  >
                    {getPreviewTranslation(activePreviewLocale, 'continueTo', {
                      name: resolvedName,
                    })}
                  </button>
                </div>
              ) : null}
            </div>

            <div
              id="previewMeta"
              className="flex items-center justify-between gap-3 px-1 text-xs"
            >
              <span className="inline-flex items-center gap-1.5 text-neutral-500">
                <Globe size={13} className="shrink-0" />
                {LOCALE_LABELS[activePreviewLocale] || activePreviewLocale}
              </span>
              {branding.showPoweredBy ? (
                <span className="text-neutral-400" data-powered-by>
                  {getPreviewTranslation(activePreviewLocale, 'poweredBy')}
                </span>
              ) : null}
            </div>
          </div>

          <p className="text-xs text-neutral-500">
            Preview of your unsaved changes. Buttons here do nothing.
          </p>
        </aside>
      </form>
    </div>
  );
};

